import { Volunteer } from './../../../../modal/person.modal'
import { Equipment, Horse, Pairing } from './../../../../modal/class.modal'
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  inject,
  output,
  signal
} from '@angular/core'
import { Navbar } from '../../../common/navbar/navbar.component'
import { Modal } from 'bootstrap'
import { FormBuilder, ReactiveFormsModule } from '@angular/forms'
import { AbstractRider } from '../../../../modal/person.modal'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { ClassService } from '../../../../services/class.service'
import { commonUtil } from '../../../../utils/common.util'

@Component({
  selector: 'app-rider-pairing',
  imports: [Navbar, ReactiveFormsModule, TranslatePipe],
  templateUrl: './rider-pairing.component.html',
  styleUrl: './rider-pairing.component.css'
})
export class RiderPairing implements AfterViewInit {
  private modal: Modal | undefined
  private formBuilder = inject(FormBuilder)
  private classService = inject(ClassService)
  private translateService = inject(TranslateService)
  onBack = output()
  pairings = signal<Pairing[]>([])
  riders = signal<AbstractRider[]>([])
  volunteers: Volunteer[] = []
  equipments: Equipment[] = []
  horses: Horse[] = []
  pairingForms = computed(() =>
    this.pairings().map((pairing) => {
      let group = this.formBuilder.group({
        leaderSelect: [pairing.leader?.id + ''],
        helperSelect: [pairing.sideHelper?.id + ''],
        equipmentSelect: [pairing.equipment?.id] + '',
        horseSelect: [pairing.horse?.id + '']
      })
      group.valueChanges.subscribe(() => {
        this.updateSelect()
      })
      return {
        rider: this.riders().find((rider) => rider.id === pairing.rider?.id),
        formGroup: group
      }
    })
  )
  classId?: number
  selected = {
    volunteerIds: [] as number[],
    equipmentIds: [] as number[],
    horseIds: [] as number[]
  }
  isPairingLoading = true
  isVolunteerLoading = true
  isEquipmentLoading = true
  isHorseLoading = true
  isUpdating = false

  constructor() {
    effect(() => {
      this.updateSelect()
    })
  }

  updateSelect() {
    this.selected.volunteerIds = []
    this.selected.equipmentIds = []
    this.selected.horseIds = []

    this.pairingForms().forEach((pairingForm) => {
      let group = pairingForm.formGroup
      let leaderCtrl = group.get('leaderSelect')!
      let helperCtrl = group.get('helperSelect')!
      let equipmentCtrl = group.get('equipmentSelect')!
      let horseCtrl = group.get('horseSelect')!

      if (leaderCtrl.value) {
        this.selected.volunteerIds.push(Number.parseInt(leaderCtrl.value))
      }
      if (helperCtrl.value) {
        this.selected.volunteerIds.push(Number.parseInt(helperCtrl.value))
      }
      if (equipmentCtrl.value) {
        this.selected.equipmentIds.push(Number.parseInt(equipmentCtrl.value))
      }
      if (horseCtrl.value) {
        this.selected.horseIds.push(Number.parseInt(horseCtrl.value))
      }
    })
  }

  isVolunteerSelected(id: number) {
    return this.selected.volunteerIds.includes(id)
  }

  isEquipmentSelected(id: number) {
    return this.selected.equipmentIds.includes(id)
  }

  isHorseSelected(id: number) {
    return this.selected.horseIds.includes(id)
  }

  hide() {
    this.onBack.emit()
    this.modal?.hide()
  }

  show(classId: number, riders: AbstractRider[]) {
    this.classId = classId
    this.riders.set(riders)
    this.modal?.show()
    this.getData()
  }

  getData() {
    this.getPairings()
    this.getVolunteers()
    this.getEquipments()
    this.getHorses()
  }

  getPairings() {
    if (!this.classId) {
      return
    }
    this.pairings.set([])
    this.isPairingLoading = true
    this.classService.getClassPairings(this.classId).subscribe({
      next: (pairings) => {
        this.pairings.set(pairings)
        this.isPairingLoading = false
      },
      error: () => (this.isPairingLoading = false)
    })
  }

  getVolunteers() {
    if (!this.classId) {
      return
    }
    this.volunteers = []
    this.isVolunteerLoading = true
    this.classService.getClassVolunteers(this.classId).subscribe({
      next: (volunteers) => {
        this.volunteers = volunteers.filter(vol => !vol.isCoach)
        this.isVolunteerLoading = false
      },
      error: () => (this.isVolunteerLoading = false)
    })
  }

  getEquipments() {
    if (!this.classId) {
      return
    }
    this.equipments = []
    this.isEquipmentLoading = true
    this.classService.getClassEquipments(this.classId).subscribe({
      next: (equipments) => {
        this.equipments = equipments
        this.isEquipmentLoading = false
      },
      error: () => (this.isEquipmentLoading = false)
    })
  }

  getHorses() {
    if (!this.classId) {
      return
    }
    this.horses = []
    this.isHorseLoading = true
    this.classService.getClassHorses(this.classId).subscribe({
      next: (horses) => {
        this.horses = horses
        this.isHorseLoading = false
      },
      error: () => (this.isHorseLoading = false)
    })
  }

  updatePairings() {
    if (!this.classId) {
      return
    }
    this.isUpdating = true
    let pairings = this.pairingForms().map((form) => {
      let groupValue = form.formGroup.getRawValue()
      let pairing = {
        rider: form.rider
      } as Pairing
      let oldPair = this.pairings().find(
        (pair) => pair.rider?.id === form.rider?.id
      )

      if (groupValue.leaderSelect !== oldPair?.leader?.id + '') {
        if (groupValue.leaderSelect) {
          pairing.leader = {
            id: Number.parseInt(groupValue.leaderSelect)
          }
        } else {
          pairing.leader = {}
        }
      }
      if (groupValue.helperSelect !== oldPair?.sideHelper?.id + '') {
        if (groupValue.helperSelect) {
          pairing.sideHelper = {
            id: Number.parseInt(groupValue.helperSelect)
          }
        } else {
          pairing.sideHelper = {}
        }
      }
      if (groupValue.equipmentSelect !== oldPair?.equipment?.id + '') {
        if (groupValue.equipmentSelect) {
          pairing.equipment = {
            id: Number.parseInt(groupValue.equipmentSelect)
          }
        } else {
          pairing.equipment = {}
        }
      }
      if (groupValue.horseSelect !== oldPair?.horse?.id + '') {
        if (groupValue.horseSelect) {
          pairing.horse = {
            id: Number.parseInt(groupValue.horseSelect)
          }
        } else {
          pairing.horse = {}
        }
      }

      return pairing
    })
    this.classService.updateClassPairings(this.classId, pairings).subscribe({
      next: () => {
        this.isUpdating = false
        commonUtil.alert(
          this.translateService.instant(
            'app.riderPairing.pairingUpdateSuccessfully'
          ),
          'success'
        )
      },
      error: () => (this.isUpdating = false)
    })
  }

  ngAfterViewInit(): void {
    this.modal = Modal.getOrCreateInstance('#riderPairing')
  }
}
